import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { VenueAmenityPicker } from '@/components/venue/VenueAmenityPicker'
import { VenueTagListEditor } from '@/components/venue/VenueTagListEditor'
import { VenueTriStateField } from '@/components/venue/VenueTriStateField'
import { useRegisterVenue } from '@/hooks/useRegisterVenue'
import { toApiError } from '@/lib/api/errors'
import { RESERVATION_UNIT_OPTIONS } from '@/lib/venue/courtTypes'
import { TIME_OF_DAY_OPTIONS } from '@/lib/venue/operatingHourTypes'
import { toFieldErrorMap } from '@/lib/venue/venueProfileViewModel'
import { VENUE_FACILITY_TYPE_CATALOG } from '@/lib/venue/venueRegisterTypes'
import {
  emptyVenueRegisterCourtFormState,
  emptyVenueRegisterFormState,
  toRegisterInput,
  validateVenueRegisterForm,
  type VenueRegisterFormState,
} from '@/lib/venue/venueRegisterViewModel'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 무료 구장 등록 — Figma `구장 등록`(14039-39309) 실측 근거는 자매 태스크
 * `mobile-facecock-app/task-748` 설계(§2)를 재사용. design.md §2 를 그대로 구현한다.
 *
 * 🚨 이 화면은 "이미 최소 1개 구장을 관리 중인 로그인 상태"에서만 진입한다(설계 §2 "진입 경로") —
 * `AppShell` 사이드바·`VenueSelectPage` 목록 헤더가 유일한 진입점이다.
 * 🚨 결제·이용권 구매·구독 단계가 없다 — 등록 버튼 문구는 "무료로 구장 등록하기" 하나뿐이다.
 */
export function VenueRegisterPage() {
  const navigate = useNavigate()
  const registerMutation = useRegisterVenue()

  const [form, setForm] = useState<VenueRegisterFormState>(emptyVenueRegisterFormState())
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function updateField<K extends keyof VenueRegisterFormState>(key: K, value: VenueRegisterFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function addCourt() {
    setForm((current) => ({ ...current, courts: [...current.courts, emptyVenueRegisterCourtFormState()] }))
  }

  function removeCourt(index: number) {
    setForm((current) => ({ ...current, courts: current.courts.filter((_, i) => i !== index) }))
  }

  function updateCourt(index: number, patch: Partial<VenueRegisterFormState['courts'][number]>) {
    setForm((current) => ({
      ...current,
      courts: current.courts.map((court, i) => (i === index ? { ...court, ...patch } : court)),
    }))
  }

  async function handleSubmit() {
    const clientErrors = validateVenueRegisterForm(form)
    if (clientErrors.length > 0) {
      setFieldErrors(toFieldErrorMap(clientErrors))
      return
    }
    setFieldErrors({})

    try {
      const result = await registerMutation.mutateAsync(toRegisterInput(form))
      navigate('/venues/register/complete', {
        replace: true,
        state: { venueId: result.venueId, name: result.name, nextSteps: result.nextSteps },
      })
    } catch (error) {
      const apiError = toApiError(error)
      if (apiError.fieldErrors) {
        setFieldErrors(toFieldErrorMap(apiError.fieldErrors))
      }
    }
  }

  // 필드 오류(400)면 이미 각 입력 옆에 표시되므로 배너를 중복으로 띄우지 않는다 — 409(등록 상한)·
  // 그 밖의 실패만 배너로 안내한다(`VenueProfileEditPage.tsx` 와 같은 규칙).
  const submitError =
    registerMutation.isError && !toApiError(registerMutation.error).fieldErrors
      ? toApiError(registerMutation.error)
      : null

  return (
    <>
      <h2 className="mb-1 text-lg font-semibold text-ink-700">새 구장 등록</h2>
      <p className="mb-5 text-sm text-slateish-600">
        결제나 이용권 구매 없이 무료로 구장을 등록할 수 있습니다.
      </p>

      <div className="mb-5 space-y-3">
        <Alert tone="info">
          기본 정보만으로 시작하고, 코트·요금은 나중에 채울 수 있어요.
        </Alert>
        {submitError ? <Alert tone="danger">{submitError.message}</Alert> : null}
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader title="기본 정보" />
          <CardBody className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="register-name">
                구장 이름
              </label>
              <Input
                id="register-name"
                value={form.name}
                aria-invalid={Boolean(fieldErrors.name)}
                onChange={(event) => {
                  updateField('name', event.target.value)
                }}
              />
              <FieldError message={fieldErrors.name} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="register-facility-type">
                시설 종류
              </label>
              <Select
                id="register-facility-type"
                value={form.facilityType}
                onChange={(event) => {
                  updateField('facilityType', event.target.value as VenueRegisterFormState['facilityType'])
                }}
              >
                {VENUE_FACILITY_TYPE_CATALOG.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="register-address">
                  주소
                </label>
                <Input
                  id="register-address"
                  value={form.address}
                  aria-invalid={Boolean(fieldErrors.address)}
                  onChange={(event) => {
                    updateField('address', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.address} />
              </div>
              <div>
                <label
                  className="mb-1.5 block text-sm font-medium text-ink-700"
                  htmlFor="register-detail-address"
                >
                  상세 주소
                </label>
                <Input
                  id="register-detail-address"
                  value={form.detailAddress}
                  aria-invalid={Boolean(fieldErrors.detailAddress)}
                  onChange={(event) => {
                    updateField('detailAddress', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.detailAddress} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="register-contact">
                연락처
              </label>
              <Input
                id="register-contact"
                value={form.contact}
                aria-invalid={Boolean(fieldErrors.contact)}
                onChange={(event) => {
                  updateField('contact', event.target.value)
                }}
                placeholder="02-000-0000"
              />
              <FieldError message={fieldErrors.contact} />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="위치·시설 안내" />
          <CardBody className="space-y-4">
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="register-location-guide"
              >
                위치 안내(찾아오는 방법)
              </label>
              <Textarea
                id="register-location-guide"
                rows={2}
                value={form.locationGuide}
                aria-invalid={Boolean(fieldErrors.locationGuide)}
                onChange={(event) => {
                  updateField('locationGuide', event.target.value)
                }}
                placeholder="예: 2호선 강남역 2번 출구 도보 5분"
              />
              <FieldError message={fieldErrors.locationGuide} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <VenueTriStateField
                label="주차 가능 여부"
                value={form.parkingAvailable}
                onChange={(value) => {
                  updateField('parkingAvailable', value)
                }}
              />
              <VenueTriStateField
                label="샤워실"
                value={form.showerAvailable}
                onChange={(value) => {
                  updateField('showerAvailable', value)
                }}
              />
              <VenueTriStateField
                label="탈의실"
                value={form.lockerRoomAvailable}
                onChange={(value) => {
                  updateField('lockerRoomAvailable', value)
                }}
              />
            </div>
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink-700">편의시설</span>
              <VenueAmenityPicker
                value={form.amenities}
                onChange={(codes) => {
                  updateField('amenities', codes)
                }}
              />
              <FieldError message={fieldErrors.amenities} />
            </div>
            <VenueTagListEditor
              label="대여 장비"
              items={form.rentalEquipments}
              onChange={(items) => {
                updateField('rentalEquipments', items)
              }}
            />
            <FieldError message={fieldErrors.rentalEquipments} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="이용 안내" />
          <CardBody>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="register-usage-guide">
              이용 안내(규칙·주의사항)
            </label>
            <Textarea
              id="register-usage-guide"
              rows={3}
              value={form.usageGuide}
              aria-invalid={Boolean(fieldErrors.usageGuide)}
              onChange={(event) => {
                updateField('usageGuide', event.target.value)
              }}
            />
            <FieldError message={fieldErrors.usageGuide} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="코트 정보 (선택)"
            description="지금 등록하지 않아도 됩니다. 등록 후 코트 관리 화면에서 언제든 추가할 수 있어요."
          />
          <CardBody className="space-y-4">
            {form.courts.map((court, index) => (
              <div key={index} className="space-y-3 rounded-lg border border-slateish-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <label
                      className="mb-1.5 block text-sm font-medium text-ink-700"
                      htmlFor={`register-court-${index}-name`}
                    >
                      코트 이름
                    </label>
                    <Input
                      id={`register-court-${index}-name`}
                      value={court.name}
                      aria-invalid={Boolean(fieldErrors[`courts[${index}].name`])}
                      onChange={(event) => {
                        updateCourt(index, { name: event.target.value })
                      }}
                    />
                    <FieldError message={fieldErrors[`courts[${index}].name`]} />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-6"
                    onClick={() => {
                      removeCourt(index)
                    }}
                  >
                    삭제
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-ink-700"
                      htmlFor={`register-court-${index}-start`}
                    >
                      운영 시작
                    </label>
                    <Select
                      id={`register-court-${index}-start`}
                      value={court.operatingStartTime}
                      onChange={(event) => {
                        updateCourt(index, { operatingStartTime: event.target.value })
                      }}
                    >
                      {TIME_OF_DAY_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-ink-700"
                      htmlFor={`register-court-${index}-end`}
                    >
                      운영 종료
                    </label>
                    <Select
                      id={`register-court-${index}-end`}
                      value={court.operatingEndTime}
                      aria-invalid={Boolean(fieldErrors[`courts[${index}].operatingEndTime`])}
                      onChange={(event) => {
                        updateCourt(index, { operatingEndTime: event.target.value })
                      }}
                    >
                      {TIME_OF_DAY_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Select>
                    <FieldError message={fieldErrors[`courts[${index}].operatingEndTime`]} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-ink-700"
                      htmlFor={`register-court-${index}-unit`}
                    >
                      예약 가능 시간 단위
                    </label>
                    <Select
                      id={`register-court-${index}-unit`}
                      value={court.reservationUnitMinutes}
                      onChange={(event) => {
                        updateCourt(index, { reservationUnitMinutes: Number(event.target.value) })
                      }}
                    >
                      {RESERVATION_UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-ink-700"
                      htmlFor={`register-court-${index}-max`}
                    >
                      최대 예약 시간(분)
                    </label>
                    <Input
                      id={`register-court-${index}-max`}
                      inputMode="numeric"
                      value={court.maxReservationMinutes}
                      aria-invalid={Boolean(fieldErrors[`courts[${index}].maxReservationMinutes`])}
                      onChange={(event) => {
                        updateCourt(index, { maxReservationMinutes: event.target.value })
                      }}
                    />
                    <FieldError message={fieldErrors[`courts[${index}].maxReservationMinutes`]} />
                  </div>
                </div>
              </div>
            ))}

            <Button type="button" variant="secondary" onClick={addCourt}>
              코트 추가
            </Button>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate(-1)
            }}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={registerMutation.isPending}
          >
            {registerMutation.isPending ? '등록 중…' : '무료로 구장 등록하기'}
          </Button>
        </div>
      </div>
    </>
  )
}
